import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, FormGroup, Label, Input, Card, CardBody, Button, Spinner, Table } from "reactstrap";
import API from "../../utils/API";
import Select from "react-select";
import { formatFieldTitle } from "../../components/utils";
import DateRange from "../../components/formfields/DateRange";
import LeadStatusSelect from "../../components/formfields/LeadStatusSelect";
import PublisherSelect from "../../components/formfields/PublisherSelect";
import LenderSelect from "../../components/formfields/LenderSelect";
import { useHistory, useLocation } from "react-router-dom";

const DownloadLeadsAdHocQuery = () => {
  const history = useHistory();
  const location = useLocation();
  const isHydratingRef = useRef(true);

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSpecification, setProductSpecification] = useState([]);
  const [loadingSpec, setLoadingSpec] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [filterValues, setFilterValues] = useState({});
  const [enabledFilters, setEnabledFilters] = useState({});
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    // Fetch products on component mount
    setLoadingProducts(true);
    API.get('/system/products')
      .then((response) => {
        if (response.data && response.data.data) {
          setProducts(response.data.data);
        }
        setLoadingProducts(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoadingProducts(false);
      });
  }, []);

  useEffect(() => {
    isHydratingRef.current = true;
    const params = new URLSearchParams(location.search);
    const stateParam = params.get("state");

    if (!stateParam) {
      isHydratingRef.current = false;
      return;
    }

    try {
      const decoded = JSON.parse(stateParam);
      const productId = decoded?.product ?? null;
      const filters = decoded?.filters ?? null;

      if (productId) {
        setSelectedProduct(Number(productId));
      }

      if (filters) {
        setEnabledFilters(filters.enabledFilters || {});
        setFilterValues(filters.filterValues || {});
      }
    } catch (e) {
      console.error("Failed to parse report state from URL:", e);
    } finally {
      isHydratingRef.current = false;
    }
  }, [location.search]);

  useEffect(() => {
    if (isHydratingRef.current) return;

    const params = new URLSearchParams(location.search);
    const state = {};

    if (selectedProduct) {
      state.product = selectedProduct;
    }

    if (Object.keys(enabledFilters).length > 0 || Object.keys(filterValues).length > 0) {
      state.filters = {
        enabledFilters,
        filterValues,
      };
    }

    if (Object.keys(state).length === 0) {
      params.delete("state");
    } else {
      params.set("state", JSON.stringify(state));
    }

    const search = params.toString();
    const nextSearch = search ? `?${search}` : "";
    if (nextSearch === location.search) {
      return;
    }

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    history.replace({
      pathname: location.pathname,
      search: nextSearch,
    });

    window.requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
    });
  }, [selectedProduct, enabledFilters, filterValues, history, location.pathname, location.search]);

  const handleProductSelect = (productId) => {
    if (productId === selectedProduct) {
      return;
    }
    setSelectedProduct(productId);
    setFilterValues({}); // Reset filter values when product changes
    setEnabledFilters({}); // Reset enabled filters when product changes
    setProductSpecification([]); // Clear previous specification
    setError(null); // Clear any previous errors
    setPreviewResults([]); // Clear preview results
    setTotalCount(null); // Clear total count
  };

  useEffect(() => {
    if (selectedProduct) {
      setLoadingSpec(true);
      setError(null);
      API.get(`/system/productspecification/${selectedProduct}`)
        .then((response) => {
          // Check if response has error flag
          if (response.data && response.data.error) {
            setProductSpecification([]);
            setError(
              response.data.data?.message ||
              "Product specification is not available for this product."
            );
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            setProductSpecification(response.data.data);
            setError(null);
          } else {
            setProductSpecification([]);
            setError("Product specification is not available for this product.");
          }
          setLoadingSpec(false);
        })
        .catch((error) => {
          console.error("Error fetching product specification:", error);
          setProductSpecification([]);
          // Handle different error response structures
          const errorMessage =
            error.response?.data?.data?.message ||
            error.response?.data?.message ||
            error.message ||
            "Failed to load product specification. Please try again.";
          setError(errorMessage);
          setLoadingSpec(false);
        });
    } else {
      setProductSpecification([]);
      setError(null);
    }
  }, [selectedProduct]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterValues({
      ...filterValues,
      [name]: value,
    });
  };

  const handleRangeChange = (fieldName, type, value) => {
    const currentRange = filterValues[fieldName] || { min: '', max: '' };
    const newRange = {
      ...currentRange,
      [type]: value,
    };
    setFilterValues({
      ...filterValues,
      [fieldName]: newRange,
    });
  };

  const handleMultiSelectChange = (fieldName, selectedOptions) => {
    // Extract values from react-select format
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setFilterValues({
      ...filterValues,
      [fieldName]: values,
    });
  };

  const handleSpecialFieldChange = (fieldName, value) => {
    setFilterValues({
      ...filterValues,
      [fieldName]: value,
    });
  };

  const buildPayload = () => {
    const payload = {
      product: selectedProduct,
      filter: [],
    };

    // Add only enabled filters that have values to the filter array
    productSpecification.forEach(field => {
      const isEnabled = enabledFilters[field.name] || false;
      if (!isEnabled) return;

      const value = filterValues[field.name];
      
      // Check if the filter has a value
      let hasValue = false;
      if (field.range && Array.isArray(field.range) && field.range.length === 2) {
        hasValue = value && (value.min || value.max);
      } else if (field.type === "options" || field.type === "leadstatus" || field.type === "publisher" || field.type === "lender") {
        hasValue = Array.isArray(value) ? value.length > 0 : (value && value.length > 0);
      } else if (field.type === "daterange") {
        hasValue = value && value.toString().trim().length > 0;
      } else {
        hasValue = value && value.toString().trim().length > 0;
      }

      if (hasValue) {
        // Add filter to the filter array with filtername and filtervalue
        payload.filter.push({
          filtername: field.name,
          filtervalue: value,
        });
      }
    });

    return payload;
  };

  const handleSearch = () => {
    if (!selectedProduct) return;

    setSearchLoading(true);
    setError(null);
    setPreviewResults([]);
    setTotalCount(null);

    // Build payload with product and filters array
    const payload = {
      ...buildPayload(),
      mode: 'preview'
    };

    API.post("/admin/report/leaddownload", payload)
      .then((response) => {
        setSearchLoading(false);
        // Response structure: { success: true, data: { data: [...], total: 17019, preview: true } }
        if (response.data && response.data.data) {
          const responseData = response.data.data;
          if (responseData.preview && responseData.total !== undefined) {
            // Preview mode response
            setPreviewResults(Array.isArray(responseData.data) ? responseData.data : []);
            setTotalCount(responseData.total);
          } else {
            // Fallback: treat as preview data
            const dataArray = Array.isArray(responseData.data) ? responseData.data : [];
            setPreviewResults(dataArray);
            setTotalCount(dataArray.length);
          }
        } else {
          // No data in response
          setPreviewResults([]);
          setTotalCount(0);
        }
      })
      .catch((error) => {
        setSearchLoading(false);
        setError(error.response?.data?.message || error.message || "Failed to search leads");
        console.error("Error searching leads:", error);
      });
  };

  const arrayToCSV = (data) => {
    if (!data || data.length === 0) {
      return '';
    }

    // Get headers from first row
    const headers = Object.keys(data[0]);
    
    // Create CSV header row
    const csvRows = [headers.map(h => `${String(h)}`).join(',')];
    
    // Create CSV data rows
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) {
          return '';
        }
        // Escape quotes and wrap in quotes
        return `${String(value)}`;
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  };

  const handleDownload = () => {
    if (!selectedProduct) return;

    setDownloadLoading(true);
    setError(null);

    // Build payload with product and filters array
    const payload = {
      ...buildPayload(),
      mode: 'file'
    };

    API.post("/admin/report/leaddownload", payload)
      .then((response) => {
        setDownloadLoading(false);
        
        if (!response.data || !response.data.data) {
          setError("No data received from server");
          return;
        }

        // Convert array to CSV
        const csvContent = arrayToCSV(response.data.data);
        
        // Create a blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const productName = products.find(p => p.id === selectedProduct)?.title || 'leads';
        const sanitizedProductName = productName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        link.download = `leads_${sanitizedProductName}_${timestamp}.csv`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        setDownloadLoading(false);
        setError(error.response?.data?.message || error.message || "Failed to download leads");
        console.error("Error downloading leads:", error);
      });
  };

  const handleFilterToggle = (fieldName) => {
    setEnabledFilters({
      ...enabledFilters,
      [fieldName]: !enabledFilters[fieldName],
    });
    // Clear filter value when disabled
    if (enabledFilters[fieldName]) {
      const newFilterValues = { ...filterValues };
      delete newFilterValues[fieldName];
      setFilterValues(newFilterValues);
    }
  };

  const renderFilterField = (field, index) => {
    const isEnabled = enabledFilters[field.name] || false;
    const handleNumberWheel = (e) => {
      // Prevent mouse wheel from changing numeric inputs while scrolling.
      e.target.blur();
    };
    
    const handleCardClick = (e) => {
      // Only enable if clicking on the card itself, not on interactive elements
      const target = e.target;
      const isInteractive = 
        target.tagName === 'INPUT' || 
        target.tagName === 'LABEL' || 
        target.closest('label') ||
        target.closest('.react-select-container') ||
        target.closest('.react-select__control') ||
        target.closest('.react-select__menu');
      
      if (!isInteractive && !isEnabled) {
        handleFilterToggle(field.name);
      }
    };

    const handleCheckboxClick = (e) => {
      e.stopPropagation(); // Prevent card click from firing
      handleFilterToggle(field.name);
    };
    
    return (
      <Col key={index} md={4} sm={12} className="mb-3">
        <Card 
          style={isEnabled ? { 
            borderWidth: '2px',
            borderColor: '#dee2e6',
            backgroundColor: '#f8f9fa',
            boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
          } : {}}
          onClick={handleCardClick}
          className={!isEnabled ? "cursor-pointer" : ""}
        >
          <CardBody>
            <FormGroup check className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={handleCheckboxClick}
                  onClick={handleCheckboxClick}
                />
                {' '}
                <strong>{formatFieldTitle(field.name)}</strong>
              </Label>
            </FormGroup>
            
            {isEnabled && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                {field.range && Array.isArray(field.range) && field.range.length === 2 && (() => {
                  const rangeValue = filterValues[field.name] || { min: '', max: '' };
                  const minLimit = field.range[0];
                  const maxLimit = field.range[1];
                  
                  return (
                    <div>
                      <div className="row">
                        <div className="col-6">
                          <Label className="small">Min</Label>
                          <Input
                            type="number"
                            step="any"
                            value={rangeValue.min || ""}
                            onChange={(e) => handleRangeChange(field.name, 'min', e.target.value)}
                            onWheel={handleNumberWheel}
                            placeholder={`Min`}
                          />
                        </div>
                        <div className="col-6">
                          <Label className="small">Max</Label>
                          <Input
                            type="number"
                            step="any"
                            value={rangeValue.max || ""}
                            onChange={(e) => handleRangeChange(field.name, 'max', e.target.value)}
                            onWheel={handleNumberWheel}
                            placeholder={`Max`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {field.type === "string" && (!field.range || !Array.isArray(field.range) || field.range.length !== 2) && (
                  <Input
                    type="text"
                    name={field.name}
                    value={filterValues[field.name] || ""}
                    onChange={handleFilterChange}
                    placeholder={`Enter ${field.name}`}
                  />
                )}
                
                {field.type === "options" && (() => {
                  const currentValue = filterValues[field.name];
                  const selectedValues = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
                  
                  // Convert field.options to react-select format
                  const selectOptions = field.options && Array.isArray(field.options)
                    ? field.options.map(option => ({ value: option, label: option }))
                    : [];
                  
                  // Convert selected values to react-select format
                  const selectedOptions = selectOptions.filter(option => 
                    selectedValues.includes(option.value)
                  );
                  
                  return (
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={selectOptions}
                      value={selectedOptions.length > 0 ? selectedOptions : null}
                      onChange={(selected) => handleMultiSelectChange(field.name, selected)}
                      isMulti
                      placeholder={`Select ${field.name}...`}
                    />
                  );
                })()}
                
                {field.type === "daterange" && (
                  <DateRange
                    name={field.name}
                    value={filterValues[field.name] || ""}
                    onChange={(name, value) => handleSpecialFieldChange(field.name, value)}
                  />
                )}
                
                {field.type === "leadstatus" && (
                  <LeadStatusSelect
                    name={field.name}
                    value={filterValues[field.name] || []}
                    onChange={(name, value) => handleSpecialFieldChange(field.name, value)}
                  />
                )}
                
                {field.type === "publisher" && (
                  <PublisherSelect
                    name={field.name}
                    value={filterValues[field.name] || []}
                    onChange={(name, value) => handleSpecialFieldChange(field.name, value)}
                  />
                )}
                
                {field.type === "lender" && (
                  <LenderSelect
                    name={field.name}
                    value={filterValues[field.name] || []}
                    onChange={(name, value) => handleSpecialFieldChange(field.name, value)}
                  />
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  };

  return (
    <Container fluid className="p-0">
      <h1 className="h3 mb-3">Download Leads Ad Hoc Query</h1>
      
      <h4 className="h5 mb-3">Product</h4>
      {loadingProducts ? (
        <div className="mb-3">
          <p>Loading products...</p>
        </div>
      ) : (
        <Row className="mb-3">
          {products.map((product) => {
            const isSelected = selectedProduct === product.id;
            return (
              <Col key={product.id} md={2} sm={4} xs={6} className="mb-3">
                <Card
                  onClick={() => handleProductSelect(product.id)}
                  className="cursor-pointer"
                  style={isSelected ? {
                    borderWidth: '2px',
                    borderColor: '#dee2e6',
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
                  } : {}}
                >
                  <CardBody className="text-center p-3">
                    <strong>{product.title}</strong>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {loadingSpec && (
        <div className="mb-3">
          <p>Loading product specification...</p>
        </div>
      )}

      {!loadingSpec && selectedProduct && productSpecification.length > 0 && (() => {
        // Count enabled filters with values
        const activeFiltersCount = productSpecification.filter(field => {
          const isEnabled = enabledFilters[field.name] || false;
          if (!isEnabled) return false;
          
          const value = filterValues[field.name];
          if (field.range && Array.isArray(field.range) && field.range.length === 2) {
            // For range fields, check if min or max has a value
            return value && (value.min || value.max);
          } else if (field.type === "options" || field.type === "leadstatus" || field.type === "publisher" || field.type === "lender") {
            // For options and multi-select fields, check if array has values
            return Array.isArray(value) ? value.length > 0 : (value && value.length > 0);
          } else if (field.type === "daterange") {
            // For date range, check if value is not empty
            return value && value.toString().trim().length > 0;
          } else {
            // For string, check if value is not empty
            return value && value.toString().trim().length > 0;
          }
        }).length;

        return (
          <div className="mb-3">
            <h4 className="h5 mb-3">
              Filters
              {activeFiltersCount > 0 && (
                <span className="font-weight-bold ml-2">({activeFiltersCount})</span>
              )}
            </h4>
            <Row form>
              {productSpecification.map((field, index) =>
                renderFilterField(field, index)
              )}
            </Row>
          </div>
        );
      })()}

      {!loadingSpec && selectedProduct && productSpecification.length === 0 && !error && (
        <div className="mb-3">
          <p className="text-muted">No filters available for this product.</p>
        </div>
      )}

      {error && (
        <div className="mb-3">
          <p className="text-danger">{error}</p>
        </div>
      )}

      {selectedProduct && (
        <div className="mt-4 mb-3 text-center">
          <Button
            color="primary"
            size="lg"
            disabled={!selectedProduct || searchLoading}
            onClick={handleSearch}
          >
            {searchLoading && (
              <Spinner size="sm" className="mr-2" />
            )}
            Search
          </Button>
        </div>
      )}

      {/* Preview Results */}
      {previewResults.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="h5 mb-0">
              Results: <strong>{totalCount !== null ? totalCount.toLocaleString() : previewResults.length}</strong> leads found
              {totalCount > previewResults.length && (
                <span className="text-muted ml-2">(showing first {previewResults.length})</span>
              )}
            </h4>
            <div className="text-right">
              {totalCount !== null && totalCount > 350000 && (
                <p className="text-warning small mb-2">
                  That&apos;s a lot of data. Download is limited to 350,000 leads. Please narrow your filters.
                </p>
              )}
              <Button
                color="success"
                disabled={downloadLoading || (totalCount !== null && totalCount > 350000)}
                onClick={handleDownload}
              >
                {downloadLoading && (
                  <Spinner size="sm" className="mr-2" />
                )}
                Download CSV
              </Button>
            </div>
          </div>
          
          <Card className="mt-2">
            <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    {Object.keys(previewResults[0] || {}).map((key) => (
                      <th key={key}>{formatFieldTitle(key)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewResults.map((row, index) => (
                    <tr key={index}>
                      {Object.keys(previewResults[0] || {}).map((key) => (
                        <td key={key}>
                          {row[key] !== null && row[key] !== undefined 
                            ? String(row[key]) 
                            : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </div>
      )}

      {!searchLoading && previewResults.length === 0 && totalCount !== null && totalCount === 0 && (
        <div className="mt-4">
          <p className="text-muted">No leads found matching the criteria.</p>
        </div>
      )}
    </Container>
  );
};

export default DownloadLeadsAdHocQuery;